---
title: Anatomia di una Relazione Algebrica e di un Metodo di Indagine Criptico
published_time: 2024-09-12T22:28:53.250Z
og:
  title: Anatomia di una Relazione Algebrica e di un Metodo di Indagine Criptico
  description: Esplora il calcolo dell'area di un triangolo attraverso la formula di Erone. Analisi dettagliata con strumenti algebrici e differenziali, e nuovi metodi di indagine.
---

<script>
  import KaTeX from "$lib/components/KaTeX.svelte"
</script>

Pensando a un laboratorio di matematica dal titolo: Alla riscoperta della relazione di Erone con strumenti matematici insoliti. Il corpus di idee presenti nella relazione in oggetto è molto ampio. Com'è noto, essa permette di calcolare l'area di un triangolo qualsiasi, \(A\), con lati assegnati \(\alpha, \beta, \gamma\) e visto che:

<KaTeX value='A=r(α+β+γ)/2' />

può essere scritta:

<div class="max-w-[80vw] mx-auto">

![image_1](image_1)

</div>

È composta con ingredienti alla portata di tutti coloro che abbiano un minimo di confidenza con la matematica. Gli elementi essenziali sono le variabili che rappresentano i **lati di un generico triangolo piano** e **il raggio della circonferenza inscritta**, uniti dalle quattro operazioni aritmetiche: somma, sottrazione, divisione, moltiplicazione, estrazione di radice e elevamento a potenza, oltre al segno di uguaglianza.

Sono tutte e sole le operazioni fondamentali del calcolo matematico che conferiscono alla relazione lo status di Teorema di Erone. Esso è inserito tra i dodici più famosi riportati nella [pubblicazione di William Dunham](http://www.unidformazione.com/formula-di-erone-definizione-e-applicazione-in-geometria/).

Per le indagini successive è necessario operare una quadratura per eliminare la radice quadrata, operazione necessaria per quello che si dirà in seguito. Erone ha sviluppato per l’occasione un calcolo semplificato della radice quadrata e ha introdotto i numeri che portano il suo nome: _numeri interi per i lati del triangolo affinché il radicando della formula risulti un quadrato perfetto_ (per esempio 12, 13, 5).

> È evidente la relazione dei numeri di Erone con le terne pitagoriche. Eliminando la radice quadrata, però, bisogna evitare le terne di numeri che non rappresentano un triangolo.

Con piccoli accorgimenti algebrici si può ottenere l'espressione algebrica equivalente seguente, in forma implicita, che sarà indicata con _E_.

<div class="max-w-[80vw] mx-auto">

![image_2](image_2)

</div>

_E_ è pronta per essere trattata con **strumenti analitici differenziali**. In parallelo con lo strumento standard sarà usato un secondo strumento che verrà presentato al momento opportuno per non appesantire la presente indagine preliminare.

Esso non ha ancora un nome, ma può essere ricavato facilmente per ogni data espressione algebrica usando il calcolo differenziale. Il calcolo differenziale è uno strumento eccellente per verificare se, in determinati contesti, e per qualche triangolo nel caso di _E_, si può ottenere l'**annullamento della derivata prima o seconda**.

La derivata, la prima, rispetto a una variabile fa pensare ai punti estremi, punti di massimo e minimo o di flesso. L'operazione di derivazione di _E_ rispetto a una variabile è in effetti una derivata parziale - ma nel seguito continuerò a chiamarla derivata.

_E_ non contiene espressamente operazioni differenziali, ma vista l'efficacia del calcolo nasce spontanea la sua applicazione a _E_ per estrarre informazioni non visibili a prima vista. Ecco la derivata prima _E'_ di _E_, rispetto ad \(\alpha\), in forma implicita:

<div class="max-w-[80vw] mx-auto">

![image_3](image_3)

</div>

_E'_ è un oggetto interessante che non è facile associare all'immagine di pendenza tipica fornita dalla derivata di una funzione dipendente da una sola variabile. Con le precisazioni di cui sopra, calcolo anche la derivata seconda, _E''_.

<div class="max-w-[80vw] mx-auto">

![image_4](image_4)

</div>

Per agevolare i calcoli algebrici con un computer, introduco il vettore `[E, E', E'']` che verrà confrontato con il vettore `[E, I',I'']` dove _I'_ e _I''_ sono gli invarianti che entrano in gioco per la prima volta e che saranno meglio definiti in una prossima comunicazione. La presenza degli apici su _I_ non significa che da _I'_ si passa a _I''_ come si fa per passare da _E'_ a _E''_. È opportuno definire la funzione vettoriale:

<KaTeX value='w(r, α, β, γ) =' />
<div class="max-w-[80vw] mx-auto">

![image_5a](image_5a)

</div>
<div class="max-w-[80vw] mx-auto">

![image_5b](image_5b)

</div>

Per funzioni implicite a più variabili, la procedura per la ricerca degli estremanti deve seguire un diverso protocollo, ma in questo contesto divulgativo è importante soffermarsi su alcuni dettagli prima di definire correttamente gli invarianti _I'_ e _I''_.

Un generico triangolo con relativa circonferenza inscritta di raggio \(r\) può essere indicato così:

<KaTeX value='[r, α, β, γ]' />

In presenza di angolo retto, usando le variabili \(a, b\), diventa:

<div class="max-w-[80vw] mx-auto">

![image_7](image_7)

</div>

Il calcolo della relativa:

<div class="max-w-[80vw] mx-auto">

![image_8](image_8)

</div>

mostra, per \(a=2b\), i seguenti risultati:

<div class="max-w-[80vw] mx-auto">

![image_9](image_9)

</div>

Si può notare che la prima colonna delle due righe della matrice di cui sopra mostra uno zero, ciò significa che per i triangoli considerati la formula di Erone è ovviamente verificata.
Per \(a=2b\) si ottiene zero anche per _I'_, oltre che _E''_, quindi la quaterna

<div class="max-w-[80vw] mx-auto">

![image_10](image_10)

</div>

annulla _E_ e _I'_. Per ora l'annullarsi di _E''_ è da considerare solo una coincidenza.

## Altri Casi Notevoli

La seguente classe di triangoli scaleni conferma il risultato precedente:

<div class="max-w-[80vw] mx-auto">

![image_11](image_11)

</div>

Invece, per la classe dei triangoli equilateri si ottiene:

<div class="max-w-[80vw] mx-auto">

![image_12](image_12)

</div>

Si noti che, benché un triangolo equilatero sia ricco di simmetrie, la derivata prima, _E'_, è diversa da zero!
Il seguente triangolo isoscele ripropone uno schema simile a quello relativo ai triangoli rettangoli già analizzato, eccolo:

<div class="max-w-[80vw] mx-auto">

![image_13](image_13)

</div>

Una novità è rappresentata dal seguente triangolo isoscele:

<div class="max-w-[80vw] mx-auto">

![image_14](image_14)

</div>

Un triangolo isoscele con misure speciali legate alla sezione aurea: visto il segno positivo della derivata seconda _E''_, dovrebbe essere considerato un punto di minimo?

## Conclusioni Parziali in Attesa di Ulteriori Sviluppi

L'invariante _I'_ non pretende in alcun modo di sostituirsi alla relazione di _E_ dalla quale discende. È comprensibile la situazione di rigetto che esso crea per il fatto che, come è stato mostrato in molti casi, si annulla come _E_ quando utilizza tutte le altre variabili presenti in _E_, eccetto quella che in _I'_ non è presente per costruzione. In assenza di una dimostrazione generale, si può comunque dire che per l'ampia varietà di triangoli che soddisfano la formula di Erone e la relazione indicata come _I'_, non si può parlare di semplici coincidenze.

Di certo, _I'_ non può essere considerata indipendente dalla relazione dalla quale discende; forse deve essere vista come operazione su particolari simmetrie dei triangoli, una rottura di simmetria. La ricerca di senso in presenza del rigore deve essere abbinata a qualche altro criterio, forse all’immaginazione.
